import { z } from 'zod';

import type { ListDefinitions } from '../../slate-api';

export type BlockType = 'P' | 'BULLET' | 'NUMBERED';

export type Section = 'HEAD' | 'BODY' | 'FOOT';

export type SelectionStatus = 'NONE' | 'CONTEXT' | 'SELECTED';

export const SELECTION_STATUS_NONE = 'NONE' satisfies SelectionStatus;
export const SELECTION_STATUS_CONTEXT = 'CONTEXT' satisfies SelectionStatus;
export const SELECTION_STATUS_SELECTED = 'SELECTED' satisfies SelectionStatus;

export type CellPosition = {
  column: number;
  row: number;
};

export type CellRange = {
  end: CellPosition;
  start: CellPosition;
};

export type Context = {
  cellPath: CellRange[];
  listDefinitions: ListDefinitions;
  path: number[];
  section: Section;
};

export type SegmentStyle = {
  bold?: true;
  fontSize?: number;
  italic?: true;
  strikethrough?: true;
  underline?: true;
};

export type ExtractedTextSegment = {
  edit: 'insert' | 'none' | 'remove';
  text: string;
  /**
   * v2 selection mentions only. When `true`, this segment falls inside
   * the user's selected range. Used by the serializer to wrap the run
   * in `<selected>…</selected>` in DETAIL lines. Marker-glyph segments
   * (`⟪` / `⟫`) carry this flag too so they're skipped from DETAIL
   * output (DETAIL uses tags, not glyphs). ORIGINAL/EDITED lines emit
   * all segment text as-is, so the markers naturally appear there.
   */
  selected?: true;
  style?: SegmentStyle;
};

/**
 * A single document block before it is serialized to:
 * >|id|part-index|selection-status|section|table-path|type|list-depth|list-marker|edit-status|content|
 */
type BaseExtractedParagraph = {
  cellRanges: CellRange[];
  /** Font size of the first non-whitespace text span. Used for heading detection in expandParagraphs. */
  fontSize: number;
  listLevel: number;
  listMarker: string;
  /**
   * A string that identifies the location of this paragraph.
   * - For AlineEditor documents: the paragraph's locationId (12-char alphanumeric)
   * - For PDF documents: page and coordinates in format `page:x1:y1:x2:y2`
   */
  locationAnchor: string;
  /**
   * Which section of the document this paragraph belongs to:
   * - HEAD: Document header
   * - BODY: Main document body
   * - FOOT: Document footer
   */
  section: Section;
  segments: ExtractedTextSegment[];
  type: BlockType;
  /** Index when paragraph is split by partial selection. Default: 0 */
  partIndex?: number;
  /** Role in selection context. Default: 'NONE' */
  selectionStatus?: SelectionStatus;
};

export type AlineExtractedParagraph = BaseExtractedParagraph & {
  path: number[];
  source: 'Aline';
};

export type PdfExtractedParagraph = BaseExtractedParagraph & {
  source: 'Pdf';
};

export type ExtractedParagraph = AlineExtractedParagraph | PdfExtractedParagraph;

export type ExtractedDocument = {
  paragraphs: ExtractedParagraph[];
};

export type ExtractedContext = {
  documentId: string;
  paragraphs: ExtractedParagraph[];
};

/**
 * v2 selection context. Same shape as ExtractedContext, plus an optional
 * focusSuggestionId carrying the identity of the suggestion marker the user
 * is asking to refine. The paragraphs themselves still mark the focus span
 * via partIndex + selectionStatus = 'SELECTED', so v2 stays
 * format-compatible with serializeExtractedParagraphs and the same
 * DOCUMENT_INPUT_SPEC the LLM already understands.
 */
export type ExtractedContextV2 = {
  documentId: string;
  focusSuggestionId: string | null;
  paragraphs: ExtractedParagraph[];
};

// Zod schemas for validation and encoding/decoding

export const CellPositionSchema = z.object({
  column: z.number(),
  row: z.number(),
});

export const CellRangeSchema = z.object({
  end: CellPositionSchema,
  start: CellPositionSchema,
});

export const SegmentStyleSchema = z
  .object({
    bold: z.literal(true).optional(),
    fontSize: z.number().optional(),
    italic: z.literal(true).optional(),
    strikethrough: z.literal(true).optional(),
    underline: z.literal(true).optional(),
  })
  .optional();

export const ExtractedTextSegmentSchema = z.object({
  edit: z.enum(['none', 'insert', 'remove']),
  selected: z.literal(true).optional(),
  style: SegmentStyleSchema,
  text: z.string(),
});

const BaseExtractedParagraphSchema = z.object({
  cellRanges: z.array(CellRangeSchema),
  fontSize: z.number(),
  listLevel: z.number(),
  listMarker: z.string(),
  locationAnchor: z.string(),
  partIndex: z.number().optional(),
  section: z.enum(['HEAD', 'BODY', 'FOOT']),
  segments: z.array(ExtractedTextSegmentSchema),
  selectionStatus: z.enum(['NONE', 'CONTEXT', 'SELECTED']).optional(),
  type: z.enum(['P', 'BULLET', 'NUMBERED']),
});

export const ExtractedParagraphSchema = z.discriminatedUnion('source', [
  BaseExtractedParagraphSchema.extend({
    path: z.array(z.number()),
    source: z.literal('Aline'),
  }),
  BaseExtractedParagraphSchema.extend({
    source: z.literal('Pdf'),
  }),
]);

export const ExtractedContextSchema = z.object({
  documentId: z.string(),
  paragraphs: z.array(ExtractedParagraphSchema),
});

export const ExtractedContextV2Schema = z.object({
  documentId: z.string(),
  focusSuggestionId: z.string().nullable(),
  paragraphs: z.array(ExtractedParagraphSchema),
});
